import API from '@/utils/API'
import { verifyResultKeys, verifyResultReasons } from '@/utils/VerifyResults'
import { ActionContext } from 'vuex'

const state: State = {
  depositAccount: {
    family_name: null,
    first_name: null,
    family_name_kana: null,
    first_name_kana: null,
    zip_code: null,
    address: null,
    mail_address: null,
  },
  verifyResults: [],
}

interface DepositAccount {
  family_name: string | null
  first_name: string | null
  family_name_kana: string | null
  first_name_kana: string | null
  zip_code: string | null
  address: string | null
  mail_address: string | null
}

interface State {
  depositAccount: DepositAccount
  verifyResults: any[]
}

type ModuleActionContext = ActionContext<State, {}>

const isInputCompleted = (state: State) => {
  const input = state.depositAccount
  return (
    input.family_name &&
    input.first_name &&
    input.family_name_kana &&
    input.first_name_kana &&
    input.zip_code &&
    input.address &&
    input.mail_address
  )
}

function isKatakana(value: string | null) {
  if (value == null) return false

  if (value.match(/^[ァ-ヶー]+$/)) {
    return true
  } else {
    return false
  }
}

// getters
const getters = {
  isVerifyResultExists: (state: State) => {
    return state.verifyResults && state.verifyResults.length > 0
  },
  verifyResultKeys: (state: State) => {
    return verifyResultKeys(state.verifyResults)
  },
  verifyResultReasons: (state: State) => {
    return verifyResultReasons(state.verifyResults)
  },
  canSubmitInput: (state: State) => {
    return isInputCompleted(state)
  },
}

const actions = {
  async fetchDepositAccount({ commit }: ModuleActionContext) {
    const request = API.Request.depositAccountGet()
    const response = await API.load(request)
    if (response.DepositAccount) {
      commit('setDepositAccount', response.DepositAccount)
    } else {
      console.error(response) // eslint-disable-line no-console
    }
  },
  async postDepositAccountUpdate({ commit, state }: ModuleActionContext) {
    if (!isInputCompleted(state)) {
      return
    }

    const request = API.Request.depositAccountUpdate({
      family_name: state.depositAccount.family_name,
      first_name: state.depositAccount.first_name,
      family_name_kana: state.depositAccount.family_name_kana,
      first_name_kana: state.depositAccount.first_name_kana,
      zip_code: state.depositAccount.zip_code,
      address: state.depositAccount.address,
      mail_address: state.depositAccount.mail_address,
    })

    const verifyResults = []

    // 姓カナのバリデーション
    if (!isKatakana(state.depositAccount.family_name_kana)) {
      verifyResults.push({
        key: 'family_name_kana',
        reason: '姓カナ（全角）の欄には全角カタカナで入力お願いいたします。',
      })
    }
    // 名カナのバリデーション
    if (!isKatakana(state.depositAccount.first_name_kana)) {
      verifyResults.push({
        key: 'first_name_kana',
        reason: '名カナ（全角）の欄には全角カタカナで入力お願いいたします。',
      })
    }
    if (verifyResults.length > 0) {
      commit('setVerifyResults', verifyResults)
      throw new Error()
    }

    const response = await API.load(request)

    commit('setVerifyResults', response.VerifyResults)
    if (response.VerifyResults || !response.DepositAccount) {
      throw new Error()
    }
    commit('setDepositAccount', response.DepositAccount)
    return response.DepositAccount
  },
}

const mutations = {
  setDepositAccount(state: State, depositAccount: DepositAccount) {
    state.depositAccount = depositAccount
  },
  setVerifyResults(state: State, verifyResults: any[]) {
    if (verifyResults && verifyResults.length > 0) {
      state.verifyResults = verifyResults
    } else {
      state.verifyResults = []
    }
  },
  updateDepositAccountAttr<Name extends keyof DepositAccount>(
    state: State,
    { name, value }: { name: Name; value: DepositAccount[Name] }
  ) {
    state.depositAccount[name] = value
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
